import {CouponErrorCode, getEventId} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {Tag} from '@wix/wix-ui-icons-common'
import {ButtonPriority, StatesButtonStates} from '@wix/wix-ui-tpa'
import {
  HoverStyles,
  StatesButton,
  Text,
  TextButton,
  TextButtonPriority,
  TextField,
  ThreeDotsLoader,
} from '@wix/wix-ui-tpa/cssVars'
import {useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React, {useEffect, useRef, useState} from 'react'
import {FormStep} from '../../../constants'
import {useAppActions, useAppSelector} from '../../../hooks/runtime'
import {getEvent} from '../../../selectors/event'
import {
  couponSubmitted,
  getCouponCode,
  getCouponError,
  isValidatingCoupon,
  isValidatingGiftCard,
} from '../../../selectors/invoice'
import {getReservationId} from '../../../selectors/reservation'
import {classes} from './coupon-input.st.css'
import {CouponFormState, CouponInputProps} from './interfaces'

export const CouponInput = ({mobile, currentStep}: CouponInputProps) => {
  const {applyCoupon, removeCoupon, editStep} = useAppActions()
  const {t} = useTranslation()
  const [state, setState] = useState(CouponFormState.INITIAL)
  const [couponCode, setCouponCode] = useState('')
  const iconRef = useRef(null)

  const eventId = getEventId(useAppSelector(getEvent))
  const reservationId = useAppSelector(getReservationId)
  const validatingGiftCard = useAppSelector(isValidatingGiftCard)
  const validating = useAppSelector(isValidatingCoupon)
  const submitted = useAppSelector(couponSubmitted)
  const error = useAppSelector(getCouponError)
  const appliedCouponCode = useAppSelector(getCouponCode)

  const paymentStep = currentStep === FormStep.Payment
  const submitButtonState = validating
    ? StatesButtonStates.IN_PROGRESS
    : submitted
    ? StatesButtonStates.SUCCESS
    : StatesButtonStates.IDLE
  const errorMessage = !error
    ? null
    : CouponErrorCode.ERROR_COUPON_DOES_NOT_EXIST
    ? t('summary.error.couponDoesNotExist')
    : CouponErrorCode.ERROR_COUPON_HAS_EXPIRED
    ? t('summary.error.promotionEnded')
    : CouponErrorCode.ERROR_COUPON_IS_NOT_ACTIVE_YET
    ? t('summary.error.couponNotActiveYet')
    : CouponErrorCode.ERROR_COUPON_USAGE_EXCEEDED
    ? t('summary.error.couponUsageExceeded')
    : CouponErrorCode.CUSTOM_ERROR_TOO_BUSY
    ? t('summary.error.serverBusy')
    : t('summary.error.unknown')

  useEffect(() => {
    if (appliedCouponCode !== couponCode) {
      setCouponCode(appliedCouponCode)
    }
  }, [appliedCouponCode])

  useEffect(() => {
    if (submitted && !validating) {
      setTimeout(() => {
        setState(CouponFormState.APPLIED)
      }, 1000)
    }
  }, [submitted, validating])

  useEffect(() => {
    if (state === CouponFormState.APPLIED && iconRef.current) {
      iconRef.current.focus()
    }
  }, [state])

  useEffect(() => {
    if (validatingGiftCard && !submitted) {
      setState(CouponFormState.INITIAL)
      setCouponCode('')
    }
  }, [validatingGiftCard])

  const handleSubmit = () => {
    if (validating || submitted) {
      return
    }

    if (paymentStep) {
      editStep({step: FormStep.BuyerDetails, validPaymentAdded: false})
    }

    applyCoupon(eventId, reservationId, couponCode)
  }

  const handleClear = () => {
    if (validating || submitted) {
      return
    }

    setCouponCode('')
    removeCoupon()
  }

  const handleRemove = () => {
    if (paymentStep) {
      editStep({step: FormStep.BuyerDetails, validPaymentAdded: false})
    }

    removeCoupon()
    setState(CouponFormState.EDIT)
    setCouponCode('')
  }

  const showEditView = () => setState(CouponFormState.EDIT)

  return (
    <div className={classNames(classes.container, {[classes.mobile]: mobile})}>
      {state === CouponFormState.INITIAL && (
        <TextButton
          className={classNames(classes.textButton, {[classes.desktopFont]: !mobile})}
          data-hook={DH.ADD_COUPON_BUTTON}
          prefixIcon={<Tag />}
          priority={TextButtonPriority.link}
          hoverStyle={HoverStyles.underline}
          onClick={showEditView}
        >
          {t('couponInput_addCouponCta')}
        </TextButton>
      )}
      {state === CouponFormState.EDIT && (
        <div aria-live="polite" className={classes.applying}>
          <TextField
            withClearButton
            newErrorMessage
            data-hook={DH.COUPON_CODE_INPUT}
            label={t('couponInput_label')}
            placeholder={t('couponInput_placeholder')}
            error={!!error}
            errorMessage={errorMessage}
            readOnly={validating || submitted}
            value={couponCode}
            // @ts-expect-error
            errorAppearance="BackgroundAndBorder"
            className={classes.couponInput}
            aria-label={t('a11y.couponCodeInputField')}
            maxLength={20}
            clearButtonAriaLabel={t('a11y.clearCouponCodeButton')}
            color="red"
            onChange={e => setCouponCode(e.target.value)}
            onClear={handleClear}
          />
          <StatesButton
            upgrade
            fullWidth
            className={classes.applyCouponButton}
            data-hook={DH.COUPON_CODE_SUBMIT}
            aria-label={validating || submitted ? t('a11y.couponCodeApplying') : undefined}
            state={submitButtonState}
            idleContent={t('couponInput_applyButton')}
            inProgressContent={<ThreeDotsLoader className={classes.loader} />}
            priority={ButtonPriority.secondary}
            onClick={handleSubmit}
          />
        </div>
      )}
      {state === CouponFormState.APPLIED && (
        <div className={classes.applied} data-hook={DH.COUPON_APPLIED}>
          <div className={classes.coupon}>
            <div className={classes.tag} ref={iconRef} tabIndex={0} aria-label={t('a11y.couponCodeApplied')} role="img">
              <Tag />
            </div>
            <Text className={classNames(classes.couponCode, {[classes.desktopFont]: !mobile})}>{couponCode}</Text>
          </div>
          <TextButton
            className={classNames(classes.textButton, {[classes.desktopFont]: !mobile})}
            data-hook={DH.COUPON_CODE_CLEAR}
            aria-label={t('a11y.clearCouponCodeButton')}
            priority={TextButtonPriority.link}
            hoverStyle={HoverStyles.underline}
            onClick={handleRemove}
          >
            {t('couponInput_removeButton')}
          </TextButton>
        </div>
      )}
    </div>
  )
}
